import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

// Durum Switch bileşeni (örn.)
const StatusSwitch = ({ rowData, fetchData }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setIsChecked(rowData.status === 1);
  }, [rowData]);

  const toggleStatus = async () => {
    const newStatus = isChecked ? 0 : 1;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/module-data/${rowData.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );
      setIsChecked(!isChecked);
      fetchData();
    } catch (error) {
      console.error("Durum güncellenemedi:", error);
    }
  };

  return (
    <div className="form-check form-switch text-end d-flex align-items-center justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      ></span>
    </div>
  );
};

const ModuleTableCreate = () => {
  const { slug } = useParams();
  const [moduleInfo, setModuleInfo] = useState(null);
  const [dataList, setDataList] = useState([]);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;
  const getLang = () => {
  return new URLSearchParams(window.location.search).get("lang") || "tr";
};

const withLang = (path) => {
  const lang = getLang();
  return `${path}?lang=${lang}`;
};

  // 1) Modül bilgisi: slug üzerinden çekiliyor
  useEffect(() => {
    const fetchModuleInfo = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slug}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setModuleInfo(resp.data);
      } catch (error) {
        console.error("Modül bilgisi alınamadı:", error);
      }
    };

    fetchModuleInfo();
  }, [slug]);

  // Modül bilgisini çek
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slug}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setModuleInfo(resp.data);
      } catch (err) {
        console.error("Modül bilgisi alınamadı:", err);
      }
    })();
  }, [slug]);

  // Verileri çekme fonksiyonu
  const fetchDataList = async () => {
    if (!moduleInfo) return;
    try {
      const token = localStorage.getItem("admin_token");
      const lang =
        new URLSearchParams(window.location.search).get("lang") || "tr";

      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/module-data`,
        {
          params: {
            module_id: moduleInfo.id,
            lang, // 🔥 BURASI
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setDataList(resp.data);
    } catch (err) {
      console.error("Veriler alınamadı:", err);
    }
  };

  // İlk yüklemede de çağır
  useEffect(() => {
    fetchDataList();
  }, [moduleInfo]);

  // Soft-delete handler
  const handleSoftDelete = async (id) => {
    if (
      !window.confirm(
        getText(
          "DELETE_CONFIRM",
          "Bu kaydı çöpe taşımak istediğinize emin misiniz?",
        ),
      )
    )
      return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/module-data/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchDataList();
    } catch (err) {
      console.error("Silme işlemi başarısız:", err);
    }
  };

  // 3) Tablo kolonları: Statik kolonlar (ID) -> Dinamik kolonlar (table: true) -> Diğer statik kolonlar
  const columns = useMemo(() => {
    if (!moduleInfo) return [];

    let dynamicElements = [];
    try {
      if (moduleInfo.elements) {
        dynamicElements = JSON.parse(moduleInfo.elements);
      }
    } catch (e) {
      console.error("Element JSON parse hatası:", e);
    }

    // Sadece tabloda görünmesi istenen elementleri alıyoruz:
    const dynamicCols = dynamicElements
      .filter((el) => el.table) // table true ise
      .map((el) => ({
        id: `column_${el.name}`,
        accessorFn: (row) => {
          let dataObj = {};
          try {
            dataObj = JSON.parse(row.data);
          } catch (err) {
            console.error("Data parse hatası:", err);
          }
          return dataObj[el.name] || "-";
        },
        header: el.label || el.name,
        meta: { headerClassName: "text-start", className: "text-start" },
        // Eğer size tanımlarsan boyut kısıtlayabilir. Bu satırı kaldırarak esnek yapabilirsin.
        // size: 100,
      }));

    // İlk statik kolon: ID
    const staticFirst = [
      {
        id: "column_id",
        accessorKey: "id",
        header: getText("TABLE_ID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        // size: 80,
      },
    ];

    // Diğer statik kolonlar: oluşturma tarihi, güncellenme tarihi, durum, işlemler
    const staticSecond = [
      {
        id: "column_created_at",
        accessorKey: "created_at",
        header: getText("TABLE_DATE_LABEL"),
        meta: {
          headerClassName: "text-end",
          className: "text-end date-column",
        },
        // size: 160,
        cell: (info) => {
          const rawDate = info.getValue();
          const formattedDate = new Date(rawDate).toLocaleDateString("en-GB");
          return (
            <div style={{ textAlign: "end", width: "125px" }}>
              {formattedDate}
            </div>
          );
        },
      },
      {
        id: "column_updated_at",
        accessorKey: "updated_at",
        header: getText("TABLE_UPDATE_DATE_LABEL"),
        meta: { headerClassName: "text-end", className: "text-end" },
        // size: 160,
        cell: (info) => {
          const raw = info.getValue();
          return raw ? new Date(raw).toLocaleString() : "-";
        },
      },
      {
        id: "column_status",
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        meta: { headerClassName: "text-end", className: "text-end" },
        // size: 100,
        cell: (info) => (
          <StatusSwitch rowData={info.row.original} fetchData={() => {}} />
        ),
      },
      {
        id: "column_actions",
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        // size: 120,
        cell: (info) => {
          const rowData = info.row.original;
          const recordId = rowData.id;
          const id = info.row.original.id;
          return (
            <div className="hstack gap-2 justify-content-end">
              <Link
                to={withLang(`/modules/${slug}/edit/${recordId}`)}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={getText("EDIT")}
              >
                <FiEdit3 />
              </Link>
              <button
                type="button"
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={getText("DELETE")}
                onClick={() => handleSoftDelete(id)}
              >
                <FiTrash2 />
              </button>
            </div>
          );
        },
      },
    ];

    const extraHitCol =
      moduleInfo.id === 61
        ? [
            {
              id: "column_hits",
              accessorKey: "hits", // API'den gelen alan
              header: "Hit",
              meta: { headerClassName: "text-end", className: "text-end" },
              // size: 100,
              cell: (info) => info.getValue() ?? 0,
            },
          ]
        : [];

    return [...staticFirst, ...dynamicCols, ...extraHitCol, ...staticSecond];
  }, [moduleInfo, getText, slug]);

  if (!moduleInfo) {
    return <p>Yükleniyor...</p>;
  }

  return (
    <div className="table-responsive">
      <Table data={dataList} columns={columns} />
    </div>
  );
};

export default ModuleTableCreate;
